// src/components/settings/catalog/catalogApi.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2.
//
// Everything the Catalog screen asks of Firebase, in one place.
// WRITES: only the catalog callables (saveCatalogItem, setCatalogItemArchived, saveCatalogFolder,
// setCatalogItemFolders, saveCatalogSupplier) — the screen never writes Firestore directly.
// READS: the list and its counts come from searchCatalogOffice, one page at a time (never the
// whole catalog). The editor reads ONE item, its cost doc and its price history directly, and the
// screen listens to the folder and supplier lists — all of which firestore.rules already let the
// office roles read.
import { httpsCallable } from 'firebase/functions';
import {
    collection, doc, getCountFromServer, getDoc, getDocs, limit, onSnapshot, orderBy, query, startAfter, where,
} from 'firebase/firestore';
import { db, functions } from '../../../firebase/config';

// timeoutMs: the browser gives up on a call after 70 s unless told otherwise. runCatalogJob works for up
// to ~7 minutes a call and exportCatalog up to 5 (catscreen-7a2c4e91 Stage 4), so those wait longer.
const call = async (name, data, timeoutMs) => (await httpsCallable(functions, name, timeoutMs ? { timeout: timeoutMs } : undefined)(data)).data;

export const searchCatalogOffice = (input) => call('searchCatalogOffice', input);
export const saveCatalogItem = (input) => call('saveCatalogItem', input);
export const setCatalogItemArchived = (itemId, archived) => call('setCatalogItemArchived', { itemId, archived });
export const setCatalogItemFolders = (itemId, folderIds) => call('setCatalogItemFolders', { itemId, folders: folderIds.map((folderId) => ({ folderId })) });
export const saveCatalogFolder = (input) => call('saveCatalogFolder', input);
export const saveCatalogSupplier = (input) => call('saveCatalogSupplier', input);
// catscreen-7a2c4e91 Stage 4 — the Stage 3 server doors, and the favourite star (Stage 1).
export const setCatalogItemFavorite = (itemId, favorite) => call('setCatalogItemFavorite', { itemId, favorite });
export const startCatalogBulk = (input) => call('startCatalogBulk', input, 310000);
export const runCatalogJob = (jobId) => call('runCatalogJob', { jobId }, 550000);
export const exportCatalog = (input) => call('exportCatalog', input, 310000);
export const countCatalogSupplierItems = (supplierIds) => call('countCatalogSupplierItems', { supplierIds });
// catscreen-7a2c4e91 Stage 6 — import (Stage 5 server). The file's cells go IN the request; nothing is
// stored. planCatalogImport works up to 9 minutes on a very big file, so the browser waits that long.
export const planCatalogImport = (input) => call('planCatalogImport', input, 550000);
export const confirmCatalogImport = (input) => call('confirmCatalogImport', input);
export const startCatalogImportUndo = (importJobId) => call('startCatalogImportUndo', { importJobId }, 310000);
export const listCatalogImports = (cursor) => call('listCatalogImports', cursor ? { cursor } : {});

/**
 * catscreen-7a2c4e91 Stage 6 — after an undo: the items it ARCHIVED because the import had created
 * them. Read from their "Import undone" history entries (Owner / Admin / Manager may read history).
 * → { count, items: [{ id, name }] } (names for the first `max`).
 */
export async function loadUndoArchived(orgId, undoJobId, max = 200) {
    const q = query(collection(db, `organizations/${orgId}/catalogHistory`), where('jobId', '==', undoJobId), where('kind', '==', 'archived'));
    const [countSnap, snap] = await Promise.all([getCountFromServer(q), getDocs(query(q, limit(max)))]);
    const ids = snap.docs.map((d) => d.data().itemId).filter(Boolean);
    const items = await Promise.all(ids.map(async (id) => {
        const s = await getDoc(doc(db, `organizations/${orgId}/services/${id}`));
        return { id, name: s.exists() ? s.data().name || '' : '' };
    }));
    return { count: countSnap.data().count, items };
}

// An unfinished bulk change, remembered in THIS browser so a reopened page can offer "Finish it".
// A per-viewer convenience only: the job itself lives on the server (and is safe to run again), so
// losing this memory (private window, cleared data) loses nothing but the reminder.
const jobKey = (orgId) => `spatchy.catalogJob.${orgId}`;
export function rememberJob(orgId, job) {
    try { window.localStorage.setItem(jobKey(orgId), JSON.stringify(job)); } catch (e) { /* storage blocked: no reminder */ }
}
export function recallJob(orgId) {
    try {
        const j = JSON.parse(window.localStorage.getItem(jobKey(orgId)) || 'null');
        return j && typeof j.jobId === 'string' ? j : null;
    } catch (e) { return null; }
}
export function forgetJob(orgId) {
    try { window.localStorage.removeItem(jobKey(orgId)); } catch (e) { /* nothing to forget */ }
}

/** Download a server-made signed link (direct navigation: fetch→blob is blocked by CORS from the live site). */
// catroll-5c8e2a71 — the export's CSV text (from exportCatalog's answer) → a downloaded file. Nothing
// is stored anywhere and there is no link: the file exists only in this browser. Same shape as the
// checklist PDF (utils/checklistClient.downloadPdfBytes). The server's text already starts with the
// byte-order mark Excel needs.
export function downloadCsv(csv, fileName) {
    if (typeof csv !== 'string') throw new Error("Couldn't prepare your file. Try again.");
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'catalog.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** One item and its office-only cost doc. → { item, cost } (cost null when there is none). */
export async function loadItem(orgId, itemId) {
    const [s, c] = await Promise.all([
        getDoc(doc(db, `organizations/${orgId}/services/${itemId}`)),
        getDoc(doc(db, `organizations/${orgId}/serviceCosts/${itemId}`)),
    ]);
    if (!s.exists()) return { item: null, cost: null };
    return { item: { id: s.id, ...s.data() }, cost: c.exists() ? c.data() : null };
}

/** Newest first, `size` at a time. → { entries, last } (pass `last` back for older ones). */
export async function loadHistory(orgId, itemId, after, size = 20) {
    const parts = [where('itemId', '==', itemId), orderBy('at', 'desc')];
    if (after) parts.push(startAfter(after));
    parts.push(limit(size));
    const snap = await getDocs(query(collection(db, `organizations/${orgId}/catalogHistory`), ...parts));
    return { entries: snap.docs.map((d) => ({ id: d.id, ...d.data() })), last: snap.docs.length === size ? snap.docs[snap.docs.length - 1] : null };
}

export const listenFolders = (orgId, onData, onError) =>
    onSnapshot(collection(db, `organizations/${orgId}/catalogFolders`), (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), onError);

export const listenSuppliers = (orgId, onData, onError) =>
    onSnapshot(collection(db, `organizations/${orgId}/catalogSuppliers`), (snap) => onData(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => String(a.name).localeCompare(String(b.name))),
    ), onError);

/** A server refusal already reads as a sentence; a network or unexpected failure doesn't. */
export function friendlyError(err, fallback) {
    const code = String((err && err.code) || '');
    if (/unavailable|deadline-exceeded|network/i.test(code) || /network|failed to fetch/i.test(String(err && err.message))) {
        return "Couldn't reach Spatchy. Check your connection and try again.";
    }
    if (/internal|unknown/.test(code) || !err || !err.message) return fallback;
    return err.message;
}

/** A Firestore Timestamp (or Date) → "Oct 21, 2026". */
export function shortDate(ts) {
    const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
    return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}
export function dateTime(ts) {
    const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
    return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
}
